import type {Operator, Poste, Sector} from "@/interfaces";
import {storeToRefs} from "pinia";
import {useMainStore} from "@/stores/mainStore";
import {getSortedPerimeters} from "@oplit/shared-module";

type OnSelectCallback = (
  secteur: Sector,
  remove: boolean,
  operateur?: Operator,
  idx?: number,
  field?: string,
) => void;

/**
     * @param functionToCallOnSelect : function that is called individually on each children machine of the clicked entity
     * for this function to work as intended, each component using this mixin should have a separate method calling
     * this function with the convenient method
     * e.g.
     
     // template
     :on-select-all="onSelectAll"
     
     // methods
     onSelectAll (...args) { return this.onSelectAllSectors(this.selectMachine, ...args) }
     
     */

type OnSelectAllSectorsExtraParams = {
  operateur?: Operator;
  idx?: number;
  field?: string;
  canSelectParents?: boolean;
  canSelectMachines?: boolean;
};

export const useSectors = () => {
  const {stationsAndMachinesTagsGroupedById} = storeToRefs(useMainStore());

  const onSelectAllSectors = (
    functionToCallOnSelect: OnSelectCallback,
    secteur: Sector & {
      machine_tags?: Poste[];
      movex_id?: string;
      children?: Sector[];
    },
    _: never,
    remove: boolean,
    onlyFirstLevel: boolean,
    {
      operateur,
      idx,
      field,
      canSelectParents,
      canSelectMachines,
    }: OnSelectAllSectorsExtraParams = {},
  ) => {
    /**
     * We select the current sector if it exists, and if :
     * - canSelectParents is true (to force selection of all elements)
     * - secteur.movex_id is defined (to select only sectors with a movex_id)
     * - secteur has no children or machine_tags (to select last level sectors)
     */

    const hasChildrenOrMachines =
      secteur.children?.length ||
      (canSelectMachines && secteur.machine_tags?.length);

    if (
      secteur &&
      (canSelectParents || secteur.movex_id || !hasChildrenOrMachines)
    )
      functionToCallOnSelect(secteur, remove, operateur, idx, field);

    /**
    Children selection - if no children or machines, we can stop here
    */
    if (!hasChildrenOrMachines) return;

    // Recursively call the function on all children machines
    if (canSelectMachines && secteur.machine_tags?.length) {
      secteur.machine_tags?.forEach((machineTag) => {
        const machine = {
          ...machineTag,
          // need to differentiate machine tags from sectors
          is_machine: true,
        };
        functionToCallOnSelect(machine, remove, operateur, idx, field);
      });
    }

    // Recursively call the function on all children sectors
    const sortedSectorChildren = getSortedPerimeters<Sector>(
      secteur.children || [],
    );
    sortedSectorChildren.forEach((child): void => {
      const [populatedChild] =
        stationsAndMachinesTagsGroupedById.value[child?.id] || [];
      if (!populatedChild) return;
      if (["removed", "deleted"].includes(populatedChild.status) && !remove)
        return;
      // If only first level is true, we only select the N-1 children.
      if (onlyFirstLevel) {
        return functionToCallOnSelect(
          populatedChild,
          false,
          operateur,
          idx,
          field,
        );
      }

      return onSelectAllSectors(
        functionToCallOnSelect,
        populatedChild,
        _,
        remove,
        false,
        {
          operateur: operateur,
          idx: idx,
          field: field,
          canSelectParents: canSelectParents,
          canSelectMachines: canSelectMachines,
        },
      );
    });
  };
  return {
    onSelectAllSectors,
  };
};
